import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm flex gap-3 [&>svg]:size-5 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        danger: 'border-red bg-red-100 text-dark [&>svg]:text-red',
        success: 'border-green bg-green-100 text-dark [&>svg]:text-green',
        warning: 'border-yellow bg-yellow-100 text-dark [&>svg]:text-yellow',
        info: 'border-blue bg-blue-100 text-dark [&>svg]:text-blue',
      },
      layout: {
        compact: 'items-center',
        longForm:
          "p-8 items-start [&_[data-slot='alert-description']]:space-y-4 [&_[data-slot='alert-description']>p]:my-0",
      },
    },
    defaultVariants: {
      variant: 'default',
      layout: 'compact',
    },
  },
);

const Alert = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & VariantProps<typeof alertVariants>>(
  ({ className, variant, layout, children, ...props }, ref) => {
    // Group title and description elements into AlertContent
    const wrappedChildren: React.ReactNode[] = [];
    let contentChildren: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const isTitle = child.type === AlertTitle;
        const isDescription = child.type === AlertDescription;

        if (isTitle || isDescription) {
          contentChildren.push(child);
        } else {
          // If we have accumulated content children, wrap them first
          if (contentChildren.length > 0) {
            wrappedChildren.push(
              <AlertContent key={`content-${wrappedChildren.length}`}>{contentChildren}</AlertContent>,
            );
            contentChildren = [];
          }
          wrappedChildren.push(child);
        }
      } else {
        // If we have accumulated content children, wrap them first
        if (contentChildren.length > 0) {
          wrappedChildren.push(
            <AlertContent key={`content-${wrappedChildren.length}`}>{contentChildren}</AlertContent>,
          );
          contentChildren = [];
        }
        wrappedChildren.push(child);
      }
    });

    // Handle any remaining content children
    if (contentChildren.length > 0) {
      wrappedChildren.push(<AlertContent key={`content-${wrappedChildren.length}`}>{contentChildren}</AlertContent>);
    }

    return (
      <div
        ref={ref}
        data-slot="alert"
        role="alert"
        className={cn(alertVariants({ variant, layout }), className)}
        {...props}
      >
        {wrappedChildren}
      </div>
    );
  },
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLHeadingElement, React.ComponentProps<'h5'>>(({ className, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/heading-has-content
  <h5 ref={ref} data-slot="alert-title" className={cn('line-clamp-1 min-h-4 heading-xs', className)} {...props} />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="alert-description"
      className={cn('text-dark/80 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed', className)}
      {...props}
    />
  ),
);
AlertDescription.displayName = 'AlertDescription';

const AlertContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="alert-content" className={cn('flex flex-col gap-1 flex-grow', className)} {...props} />
));
AlertContent.displayName = 'AlertContent';

const AlertAction = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="alert-action"
    className={cn('self-start flex items-start justify-end', className)}
    {...props}
  />
));
AlertAction.displayName = 'AlertAction';

export { Alert, AlertTitle, AlertDescription, AlertContent, AlertAction };
